<template>
  <main class="flex flex-col gap-4 mb-10">
    <section class="flex justify-between items-center">
      <p class="text-lg font-bold">{{ $t("quiz.teacher.questions") }}</p>
      <div class="flex gap-3">
        <!-- <Button
          id="btn-add-questions"
          :label="$t('quiz.teacher.add')"
          class="btn-primary p-button-sm"
          icon="pi pi-plus"
          :disabled="!quizId"
          @click="openQuestionOptions()"
          v-if="role === 'TEACHER'"
        /> -->
        <div
          ref="questionOptions"
          class="bg-white w-72 py-2 shadow-md rounded-md absolute mt-10 right-10"
          v-if="showQuestionOptions"
        >
          <Button
            id="btn-register-questions"
            class="btn-simple p-button-sm"
            :disabled="!quizId"
            @click="goToQuestion()"
            v-if="role === 'TEACHER'"
          >
            <v-icon name="md-add"></v-icon>
            <p class="text-[14px] ml-1">
              {{
                $t("labels.generic", {
                  pt: "Cadastrar questões",
                  en: "Create questions",
                })
              }}
            </p>
          </Button>
          <ImportCSV
            @onUpload="onUpload($event)"
            @onDownload="downloadCSV()"
            @onClose="onClose"
            :data="importData"
            :openModal="displayModal"
            btnStyle="btn-simple w-full"
            btnIcon="md-uploadfile-round"
            :buttons="{
              first: $t('labels.generic', {
                pt: 'Importar questões',
                en: 'Import questions',
              }),
              second: $t('labels.generic', {
                pt: 'Importar novo documento',
                en: 'Import new document',
              }),
            }"
          />
          <Button
            id="btn-download-csv"
            class="btn-simple"
            @click="downloadCSVTemplate"
          >
            <v-icon name="md-insertdrivefile-round"></v-icon>
            <p class="text-[14px] ml-1">
              {{
                $t("labels.generic", {
                  pt: "Baixar modelo de importação",
                  en: "Download import model",
                })
              }}
            </p>
          </Button>
        </div>
      </div>
    </section>

    <section
      class="flex flex-col md:flex-row justify-center md:justify-between items-center gap-3 bg-gray-200 font-bold p-4 rounded-md shadow-md"
    >
      <p>{{ $t("quiz.teacher.list") }}</p>
      <p v-if="!loader">
        {{ questions?.length }}
        {{ $t("labels.generic", { pt: "Questões", en: "Questions" }) }}
      </p>
    </section>

    <section v-for="(question, q) in questionsLocal" :key="q">
      <div
        v-if="!loader"
        class="bg-white rounded-lg border-b-4 border-h-primary-500 shadow-sm p-5"
      >
        <div
          v-if="question.openQuestion"
          class="bg-h-warning-100 text-h-warning-700 w-fit px-2 py-1 rounded-sm shadow-sm text-sm mb-3"
        >
          {{
            $t("labels.generic", { pt: "Questão aberta", en: "Open question" })
          }}
        </div>
        <div class="flex items-center justify-between">
          <p class="font-bold text-lg break-words w-full">
            {{ question.prompt }}
          </p>
        </div>
        <div v-for="(item, i) in question.items" :key="i" class="mb-5">
          <div class="text-gray-500 my-3 flex items-start gap-3">
            <span class="font-bold">
              <v-icon
                :name="`${
                  item.id === question.correctItem?.id
                    ? 'hi-solid-check'
                    : 'hi-x'
                }`"
                :class="`${
                  item.id === question.correctItem?.id
                    ? 'text-h-success-400'
                    : 'text-h-error-400'
                }`"
              />
            </span>
            <p class="break-words max-w-full">
              {{ item.answerText }}
            </p>
          </div>
        </div>
        <div
          class="flex justify-end items-center gap-3"
          v-if="role === 'TEACHER'"
        >
          <Button
            :id="`btn-edit-question-${q}`"
            class="btn-simple p-button-sm"
            icon="pi pi-pencil"
            :label="$t('labels.edit')"
            @click="goToQuestion(question.id)"
          />
          <Button
            :id="`btn-delete-question-${q}`"
            class="btn-delete p-button-sm"
            icon="pi pi-trash"
            :label="$t('labels.delete')"
            :loading="loadBtn[q]"
            @click="deleteQuestion(question.id, q)"
          />
        </div>
      </div>
    </section>

    <section
      class="py-10"
      v-if="!questions || (questions.length === 0 && !loader)"
    >
      <EmptyData
        :data="!questions || questions.length === 0"
        :label="emptyState.description"
        :image="emptyState.image"
        imgSize="h-24"
      />
    </section>
  </main>
  <Loader :loader="loader" loaderSize="h-48" />
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { Question } from "@/models/quiz.model";
import { CsvFile } from "@/models/user.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Parser } from "@json2csv/plainjs";
import { saveAs } from "file-saver";
import { delay } from "rxjs";
import { defineComponent, PropType } from "vue";
import { ObjectiveService } from "../../objective.service";
import { data } from "./question_template";

export default defineComponent({
  name: "formQuestions",
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
  },
  props: {
    quizId: {
      type: String,
      default: "",
    },
    role: [String],
    questions: {
      type: Array as PropType<any>,
      default: [],
    },
    objective: [Object],
  },
  data() {
    return {
      questionsLocal: [] as any,
      loadBtn: [] as any,
      loader: true as boolean,
      objectiveId: "" as string,
      importData: new CsvFile(),
      showQuestionOptions: false as boolean,
      displayModal: false as boolean,
      emptyState: {
        image: "empty_questions.svg",
        description: this.$t("quiz.teacher.empty"),
      } as any,
    };
  },
  mounted() {
    this.objectiveId = this.objective?.id
    setTimeout(() => {
      this.questionsLocal = this.questions;
      this.loader = false;
    }, 500);
  },
  methods: {
    goToQuestion(id?: string): void {
      const params = {
        quizId: this.quizId,
        objectiveId: this.objectiveId,
        id: id ?? undefined,
      } as any;
      this.go.redirectToPage("formQuestionTraditional", params);
    },
    deleteQuestion(id: string, index: number): void {
      this.loadBtn[index] = true;
      this.rest
        .deleteQuestion(this.quizId, id)
        .pipe(delay(500))
        .subscribe({
          next: () => {
            this.loadBtn[index] = false;
            const questions = this.questionsLocal;
            const filtered = questions.filter((e: any) => e.id !== id);
            this.questionsLocal = filtered as Question[];
          },
          error: () => {
            this.loadBtn[index] = false;
          },
        });
    },
    getQuestionByQuiz(): void {
      const tab = Number(this.$route.params.tab);
      if (this.quizId !== "" && tab === 3) {
        this.service.questions.pipe(delay(500)).subscribe({
          next: (response) => {
            this.loader = false;
            this.questionsLocal = response?.content;
          },
        });
        this.service.getQuestionsByQuiz(this.quizId, new QueryParams(0, 100));
      } else {
        this.loader = false;
      }
    },
    onUpload(event: any): void {
      this.displayModal = true;
      this.importData.fileError = false;
      var { files } = event.target;
      if (files[0]?.type == "text/csv") {
        const formData = new FormData();
        formData.append("file", files[0]);
        this.importData = new CsvFile();
        this.importData.fileError = false;
        this.importData.file.name = files[0]?.name;
        this.rest
          .importQuestions(this.quizId, formData)
          .pipe(delay(3000))
          .subscribe({
            next: (response) => {
              this.getQuestionByQuiz();
              if (response) {
                const csv: any = this.generics.csvToArray(response);
                this.importData.csv = response;
                this.importData.errors = csv;
              } else {
                this.displayModal = false;
              }
            },
            error: () => {
              this.importData = new CsvFile();
            },
          });
      } else {
        this.importData = new CsvFile();
      }

      document.removeEventListener("click", this.closeQuestionOptions);
    },
    downloadCSV(): void {
      const blob = new Blob([this.importData?.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      saveAs(url, `relatório_de _erros.csv`);
    },
    downloadCSVTemplate(): void {
      const csv = new Parser({ delimiter: ";" }).parse(data);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      saveAs(url, `template_question.csv`);
      this.$toast.add(ToastFactory.warn(Messages.CSV_OBS));
    },
    onClose(): void {
      this.displayModal = false;
      setTimeout(() => {
        document.addEventListener("click", this.closeQuestionOptions);
      }, 100);
    },
    openQuestionOptions(): void {
      if (!this.showQuestionOptions) {
        this.showQuestionOptions = true;
        setTimeout(() => {
          document.addEventListener("click", this.closeQuestionOptions);
        }, 100);
      }
    },
    closeQuestionOptions(event): void {
      const myDiv = this.$refs.questionOptions as HTMLElement;
      if (myDiv && !myDiv.contains(event.target as Node)) {
        this.showQuestionOptions = false;
        document.removeEventListener("click", this.closeQuestionOptions);
      }
    },
  },
});
</script>
